import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RefreshCw, Search, AlertTriangle, CheckCircle, XCircle, Globe } from "lucide-react";

interface RequestLog {
  id: string;
  created_at: string;
  method: string;
  endpoint: string;
  status_code: number | null;
  error_message: string | null;
  request_body: Record<string, unknown> | null;
  response_body: Record<string, unknown> | null;
  user_id: string | null;
  ip_address: string | null;
  duration_ms: number | null;
  user_agent: string | null;
}

export default function RequestLogs() {
  const [logs, setLogs] = useState<RequestLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedLog, setSelectedLog] = useState<RequestLog | null>(null);

  const fetchLogs = async () => {
    setLoading(true);
    let query = supabase
      .from("request_logs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);

    if (search) {
      query = query.or(`endpoint.ilike.%${search}%,error_message.ilike.%${search}%`);
    }

    if (statusFilter === "error") {
      query = query.gte("status_code", 400);
    } else if (statusFilter === "success") {
      query = query.lt("status_code", 400);
    }

    const { data, error } = await query;
    if (error) {
      console.error("Failed to fetch request logs:", error);
    } else {
      setLogs((data as RequestLog[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
  }, [statusFilter]);

  const getStatusBadge = (code: number | null) => {
    if (!code) return <Badge variant="outline">N/A</Badge>;
    if (code < 300) return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">{code}</Badge>;
    if (code < 400) return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">{code}</Badge>;
    if (code < 500) return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">{code}</Badge>;
    return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">{code}</Badge>;
  };

  const getMethodBadge = (method: string) => {
    const colors: Record<string, string> = {
      GET: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      POST: "bg-green-500/20 text-green-400 border-green-500/30",
      PUT: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      DELETE: "bg-red-500/20 text-red-400 border-red-500/30",
      PATCH: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    };
    return <Badge className={colors[method] || "bg-muted text-muted-foreground"}>{method}</Badge>;
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const errorCount = logs.filter(l => l.status_code && l.status_code >= 400).length;
  const successCount = logs.filter(l => l.status_code && l.status_code < 400).length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Globe className="h-6 w-6" />
            网络请求监控
          </h1>
          <p className="text-muted-foreground mt-1">监控 API 请求与错误日志</p>
        </div>
        <Button onClick={fetchLogs} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          刷新
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Globe className="h-4 w-4" />
            总请求数
          </div>
          <p className="text-2xl font-bold mt-1">{logs.length}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <CheckCircle className="h-4 w-4" />
            成功
          </div>
          <p className="text-2xl font-bold mt-1 text-green-400">{successCount}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-400 text-sm">
            <XCircle className="h-4 w-4" />
            错误
          </div>
          <p className="text-2xl font-bold mt-1 text-red-400">{errorCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="搜索接口或错误信息..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchLogs()}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部</SelectItem>
            <SelectItem value="error">仅错误</SelectItem>
            <SelectItem value="success">仅成功</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border border-border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">时间</TableHead>
              <TableHead className="w-[70px]">方法</TableHead>
              <TableHead>接口</TableHead>
              <TableHead className="w-[70px]">状态</TableHead>
              <TableHead>错误信息</TableHead>
              <TableHead className="w-[80px]">耗时</TableHead>
              <TableHead className="w-[120px]">IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  加载中...
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  暂无请求日志
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow
                  key={log.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedLog(log)}
                >
                  <TableCell className="text-xs text-muted-foreground">{formatTime(log.created_at)}</TableCell>
                  <TableCell>{getMethodBadge(log.method)}</TableCell>
                  <TableCell className="font-mono text-xs max-w-[200px] truncate">{log.endpoint}</TableCell>
                  <TableCell>{getStatusBadge(log.status_code)}</TableCell>
                  <TableCell className="text-xs text-red-400 max-w-[200px] truncate">
                    {log.error_message && (
                      <span className="flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        {log.error_message}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {log.duration_ms ? `${log.duration_ms}ms` : "-"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{log.ip_address || "-"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>请求详情</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">时间：</span>
                  <span>{new Date(selectedLog.created_at).toLocaleString("zh-CN")}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">方法：</span>
                  {getMethodBadge(selectedLog.method)}
                </div>
                <div>
                  <span className="text-muted-foreground">接口：</span>
                  <span className="font-mono">{selectedLog.endpoint}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">状态码：</span>
                  {getStatusBadge(selectedLog.status_code)}
                </div>
                <div>
                  <span className="text-muted-foreground">耗时：</span>
                  <span>{selectedLog.duration_ms ? `${selectedLog.duration_ms}ms` : "N/A"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">IP：</span>
                  <span>{selectedLog.ip_address || "N/A"}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">User Agent：</span>
                  <span className="text-xs break-all">{selectedLog.user_agent || "N/A"}</span>
                </div>
                {selectedLog.user_id && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">用户ID：</span>
                    <span className="font-mono text-xs">{selectedLog.user_id}</span>
                  </div>
                )}
              </div>

              {selectedLog.error_message && (
                <div>
                  <h4 className="text-sm font-medium text-red-400 mb-1">错误信息</h4>
                  <pre className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-xs text-red-300 whitespace-pre-wrap">
                    {selectedLog.error_message}
                  </pre>
                </div>
              )}

              {selectedLog.request_body && (
                <div>
                  <h4 className="text-sm font-medium mb-1">请求体</h4>
                  <pre className="bg-muted rounded-lg p-3 text-xs whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(selectedLog.request_body, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.response_body && (
                <div>
                  <h4 className="text-sm font-medium mb-1">响应体</h4>
                  <pre className="bg-muted rounded-lg p-3 text-xs whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(selectedLog.response_body, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
